import { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import {
  DownloadIcon,
  ExternalLinkIcon,
  FilmIcon,
  PaperclipIcon,
} from "lucide-react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Attachment } from "@/types/types";
import { useInboxStore } from "@/stores";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

// Only types the browser renders natively. Never html/svg: blob: URLs share the
// app origin, so opening creator-controlled markup would be an XSS vector.
const BINARY_PREVIEW_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];
// Plain-text types are read with blob.text() and rendered as escaped text in
// a <pre> — never through an iframe, so nothing in the file can execute.
const TEXT_PREVIEW_TYPES = [
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json",
];
// Any video/* plays through a <video> element (no script surface, like <img>).
// Which containers/codecs actually decode is up to the browser — mp4/webm
// everywhere, mov mostly, mkv sometimes — so the player's onError falls back
// to a download hint instead of a blank box.
const VIDEO_PREVIEW_TYPES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-m4v",
  "video/ogg",
  "video/x-matroska",
];
// Word documents: no browser renders them natively, so docx-preview draws the
// document (headings, bold, lists, tables, spacing) into an iframe that cannot
// run scripts — see renderDocx. The download keeps the original file.
const DOCX_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const PREVIEWABLE_TYPES = [
  ...BINARY_PREVIEW_TYPES,
  ...TEXT_PREVIEW_TYPES,
  ...VIDEO_PREVIEW_TYPES,
  DOCX_TYPE,
];
// Text bigger than this is cut off in the preview (download has the whole file).
const TEXT_PREVIEW_LIMIT = 1_000_000;
// Providers sometimes report a generic type for plain files — fall back to
// the extension so a ".txt" (or ".mp4") sent as application/octet-stream
// still previews.
const EXTENSION_TYPES: Record<string, string> = {
  txt: "text/plain",
  log: "text/plain",
  csv: "text/csv",
  md: "text/markdown",
  json: "application/json",
  mp4: "video/mp4",
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  webm: "video/webm",
  ogv: "video/ogg",
  mkv: "video/x-matroska",
  docx: DOCX_TYPE,
};
const GENERIC_TYPES = ["", "application/octet-stream", "binary/octet-stream"];

function effectiveType(fileType: string, fileName: string): string {
  const declared = (fileType || "").toLowerCase();
  if (!GENERIC_TYPES.includes(declared)) return declared;
  const ext = (fileName || "").split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_TYPES[ext] ?? declared;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

interface FileAttachmentProps {
  attachment: Attachment;
  messageId?: string;
  campaignCreatorId?: string;
}

export default function FileAttachment({
  attachment,
  messageId,
  campaignCreatorId,
}: FileAttachmentProps) {
  const { getToken } = useAuth();
  const { fetchAttachment } = useInboxStore();
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  // The browser refused to decode the video (unsupported container/codec).
  const [videoFailed, setVideoFailed] = useState(false);
  // The Word file couldn't be rendered (damaged, password-protected).
  const [docFailed, setDocFailed] = useState(false);
  // The fetched Word file, kept for renderDocx once the preview iframe is up.
  const docBlobRef = useRef<Blob | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  // Bumped when the dialog closes so an in-flight fetch result is discarded.
  const fetchSeq = useRef(0);

  const fileType = effectiveType(attachment.file_type, attachment.file_name);
  const isImage = fileType.startsWith("image/");
  const isVideo = fileType.startsWith("video/");
  const isText = TEXT_PREVIEW_TYPES.includes(fileType);
  const isDocx = fileType === DOCX_TYPE;
  const typeSupported = PREVIEWABLE_TYPES.includes(fileType) || isVideo;
  const previewable = !!(
    messageId &&
    campaignCreatorId &&
    attachment.attachment_id &&
    typeSupported
  );

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const openPreview = async () => {
    setOpen(true);
    if (blobUrlRef.current) return;

    const seq = ++fetchSeq.current;
    const token = await getToken();
    if (!token) {
      toast.error("Failed to load attachment");
      setOpen(false);
      return;
    }

    const blob = await fetchAttachment(
      attachment,
      messageId!,
      campaignCreatorId!,
      token,
    );
    if (seq !== fetchSeq.current) return;
    if (!blob) {
      setOpen(false);
      return;
    }

    if (isText) {
      const text = await blob.text();
      if (seq !== fetchSeq.current) return;
      setTextContent(
        text.length > TEXT_PREVIEW_LIMIT
          ? `${text.slice(0, TEXT_PREVIEW_LIMIT)}\n\n… preview cut off, download the file for the rest.`
          : text,
      );
    }
    if (isDocx) docBlobRef.current = blob;

    // Re-type a generically-typed blob so <video> and "open in new tab" see
    // the real media type (the store types it by the provider's declaration).
    const typed = blob.type === fileType ? blob : new Blob([blob], { type: fileType });
    const url = URL.createObjectURL(typed);
    blobUrlRef.current = url;
    setBlobUrl(url);
  };

  // Draws the Word file into the preview iframe once its blank page is ready.
  // A file it can't read falls back to a download hint, like an unplayable
  // video.
  const renderDocx = async (frame: HTMLIFrameElement) => {
    const page = frame.contentDocument?.body;
    const blob = docBlobRef.current;
    if (!page || !blob) return;
    const seq = fetchSeq.current;
    try {
      // No embedded web pages, and links stay plain text: nothing in a
      // creator's file gets to load or navigate anywhere.
      await renderAsync(blob, page, undefined, { renderAltChunks: false });
      page.querySelectorAll("a").forEach((a) => a.removeAttribute("href"));
    } catch {
      if (seq === fetchSeq.current) setDocFailed(true);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      fetchSeq.current++;
      const url = blobUrlRef.current;
      blobUrlRef.current = null;
      setBlobUrl(null);
      setTextContent(null);
      setVideoFailed(false);
      setDocFailed(false);
      docBlobRef.current = null;
      // Delayed revoke: a tab opened from this URL may still be loading it.
      if (url) setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = attachment.file_name || "attachment";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenInTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank");
  };

  const chipContent = (
    <>
      <span className="p-1 size-9 bg-muted-foreground/10 rounded-md flex justify-center items-center">
        {isVideo ? (
          <FilmIcon className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <PaperclipIcon className="size-4 shrink-0 text-muted-foreground" />
        )}
      </span>

      <div className="flex flex-col items-start justify-center text-xs">
        <span className="font-medium max-w-64 truncate">
          {attachment.file_name}
        </span>
        <span className="text-muted-foreground">
          {formatFileSize(attachment.size)} •{" "}
          {attachment.file_type.replace("application/", "")}
        </span>
      </div>
    </>
  );

  if (!previewable) {
    return (
      <div
        onClick={() =>
          toast.error(
            typeSupported
              ? "This attachment can't be previewed"
              : "Preview is not supported for this file type",
          )
        }
        className="flex items-center gap-2 p-0.75 pr-2.5 border rounded-lg bg-background"
      >
        {chipContent}
      </div>
    );
  }

  return (
    <>
      <ScopeWrapper>
        <button
          type="button"
          onClick={openPreview}
          title="Preview"
          className="flex items-center gap-2 p-0.75 pr-2.5 border rounded-lg bg-background text-left cursor-pointer hover:bg-muted transition-colors"
        >
          {chipContent}
        </button>
      </ScopeWrapper>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="flex flex-col sm:max-w-5xl h-[95vh] p-4 gap-4">
          <DialogHeader className="pr-10 gap-0.5">
            <DialogTitle className="text-base font-medium truncate">
              {attachment.file_name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {formatFileSize(attachment.size)} •{" "}
              {attachment.file_type.replace("application/", "")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center">
            {!blobUrl ? (
              <Skeleton className="w-full h-full flex items-center justify-center rounded-lg animate-out fade-out" />
            ) : isImage ? (
              <img
                src={blobUrl}
                alt={attachment.file_name}
                className="max-h-full max-w-full object-contain rounded-lg animate-in fade-in duration-500"
              />
            ) : isVideo ? (
              videoFailed ? (
                <p className="text-sm text-muted-foreground text-center px-6">
                  Your browser can't play this video format. Download it to
                  watch it.
                </p>
              ) : (
                <video
                  src={blobUrl}
                  controls
                  playsInline
                  preload="metadata"
                  onError={() => setVideoFailed(true)}
                  className="max-h-full max-w-full rounded-lg bg-black animate-in fade-in duration-500"
                />
              )
            ) : docFailed ? (
              <p className="text-sm text-muted-foreground text-center px-6">
                This document can't be previewed here. Download it to open
                it.
              </p>
            ) : isText ? (
              <pre className="w-full h-full overflow-auto rounded-lg border bg-muted/40 p-4 text-sm font-mono whitespace-pre-wrap break-words animate-in fade-in duration-500">
                {textContent ?? ""}
              </pre>
            ) : isDocx ? (
              <iframe
                // No allow-scripts: nothing inside the document can execute.
                sandbox="allow-same-origin"
                srcDoc='<!DOCTYPE html><body style="margin:0">'
                onLoad={(e) => renderDocx(e.currentTarget)}
                title={attachment.file_name}
                className="w-full h-full rounded-lg animate-in fade-in duration-500"
              />
            ) : (
              <iframe
                src={blobUrl}
                title={attachment.file_name}
                className="w-full h-full rounded-lg animate-in fade-in duration-500"
              />
            )}
          </div>

          <DialogFooter>
            {/* A browser tab has nothing to render for a Word file — it would
                only download it a second time. */}
            {!isDocx && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleOpenInTab}
                disabled={!blobUrl}
              >
                <ExternalLinkIcon />
                Open in new tab
              </Button>
            )}
            <Button size="sm" onClick={handleDownload} disabled={!blobUrl}>
              <DownloadIcon />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
